import { useEffect, useMemo, useState } from 'react';
import { Card, Empty, Segmented, Space, Tag, Typography } from 'antd';
import { ApartmentOutlined, FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './DetailBox.css';
import HighlightedText from './HighlightedText';
import NodeLinkList from './NodeLinkList';
import { getTextUnits } from '../utils/filerHandler';

const { Paragraph, Text, Title } = Typography;

const TAB_ITEMS = [
  { label: 'Overview', value: 'overview', icon: <InfoCircleOutlined /> },
  { label: 'Text Units', value: 'text', icon: <FileTextOutlined /> },
  { label: 'Connections', value: 'links', icon: <ApartmentOutlined /> },
];

export default function DetailBox({ node, link, details, focusNode, focusLink }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [textUnit, setTextUnit] = useState([]);
  const [tab, setTab] = useState('overview');
  const [linkedNodes, setLinkedNodes] = useState([]);
  const [linkedRelationships, setLinkedRelationships] = useState([]);

  useEffect(() => {
    setTab('overview');
    const item = node || link;
    if (!item) return;
    setTitle(node ? (node.title || node.id || 'Node Details')
      : `${link.source?.title || 'Unknown'} - ${link.target?.title || 'Unknown'}`);
    setDescription(item.description || (node ? 'A node without description.' : 'A link without description.'));
    getTextUnits(item.text_unit_ids || [], setTextUnit, details);
    setLinkedNodes(node ? node.neighbors || [] : [link.source, link.target].filter(Boolean));
    setLinkedRelationships(node ? node.links || [] : []);
  }, [node, link, details]);

  const titleKeywords = useMemo(() => title.split(' - ').filter(Boolean), [title]);

  return (
    <Card bordered={false} className="detail-box">
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <div className="detail-box__header">
          <Space orientation="vertical" size={6} style={{ width: '100%' }}>
            <Tag color={node ? 'cyan' : 'gold'} bordered={false}>
              {node ? 'Node' : 'Relationship'}
            </Tag>
            <Title level={5} className="detail-box__title">{title}</Title>
          </Space>
        </div>

        <Segmented
          block
          options={TAB_ITEMS.map((item) => ({
            label: (
              <Space size={6}>
                {item.icon}
                <span>{item.label}</span>
              </Space>
            ),
            value: item.value,
          }))}
          value={tab}
          onChange={setTab}
        />

        <div className="detail-box__body">
          {tab === 'overview' ? (
            <div className="detail-box__panel">
              <Paragraph className="detail-box__description">
                {description}
              </Paragraph>
            </div>
          ) : null}

          {tab === 'text' ? (
            <div className="detail-box__panel detail-box__panel--list">
              {textUnit && textUnit.length > 0 ? (
                textUnit.map((item, idx) => (
                  <Card key={idx} size="small" className="detail-box__text-unit">
                    <Text>
                      <HighlightedText text={item} keywords={titleKeywords} />
                    </Text>
                  </Card>
                ))
              ) : (
                <Empty description="No text units available." />
              )}
            </div>
          ) : null}

          {tab === 'links' ? (
            <div className="detail-box__panel detail-box__panel--list">
              <NodeLinkList
                nodes={linkedNodes}
                relationships={linkedRelationships}
                focusNode={focusNode}
                focusLink={focusLink}
              />
            </div>
          ) : null}
        </div>
      </Space>
    </Card>
  );
}
